import type {
  CategoryOption,
  Orientation,
  Pacing,
  ProductionLevel,
  TutorialCode,
  VideoListParams,
} from '@/api/types';

/** 7 флагов features — порядок и подписи из data.js (CAT-2). */
export type FeatureKey =
  | 'has_visual_effects'
  | 'has_3d'
  | 'has_animations'
  | 'has_motion_graphics'
  | 'has_typography'
  | 'has_sound_design'
  | 'has_ai';

export const FEATURES: ReadonlyArray<{ key: FeatureKey; label: string; url: string }> = [
  { key: 'has_visual_effects', label: 'Visual effects', url: 'vfx' },
  { key: 'has_3d', label: '3D', url: '3d' },
  { key: 'has_animations', label: 'Animations', url: 'anim' },
  { key: 'has_motion_graphics', label: 'Motion graphics', url: 'mg' },
  { key: 'has_typography', label: 'Typography', url: 'type' },
  { key: 'has_sound_design', label: 'Sound design', url: 'sound' },
  { key: 'has_ai', label: 'AI', url: 'ai' },
];

export const TUTORIAL_PLATFORMS: ReadonlyArray<{ code: TutorialCode; label: string }> = [
  { code: 'FC', label: 'Final Cut Pro' },
  { code: 'PP', label: 'Premiere Pro' },
  { code: 'DR', label: 'DaVinci Resolve' },
  { code: 'CC', label: 'CapCut' },
];

export const PACING_VALUES: readonly Pacing[] = ['slow', 'fast', 'mixed'];
export const LEVEL_VALUES: readonly ProductionLevel[] = ['low', 'mid', 'high'];
export const ORIENTATION_VALUES: readonly Orientation[] = ['horizontal', 'vertical'];

export interface CatalogFilters {
  category_ids: number[];
  transition_type_ids: number[];
  hook_ids: number[];
  pacing: Pacing[];
  production_level: ProductionLevel[];
  orientation: Orientation[];
  tutorial_platforms: TutorialCode[];
  features: FeatureKey[];
}

export type FilterGroupKey = keyof CatalogFilters;

/** Порядок групп в ряду чипов — из прототипа Catalog. */
export const FILTER_GROUP_ORDER: readonly FilterGroupKey[] = [
  'category_ids',
  'transition_type_ids',
  'pacing',
  'production_level',
  'features',
  'hook_ids',
  'orientation',
  'tutorial_platforms',
];

export const EMPTY_FILTERS: CatalogFilters = {
  category_ids: [],
  transition_type_ids: [],
  hook_ids: [],
  pacing: [],
  production_level: [],
  orientation: [],
  tutorial_platforms: [],
  features: [],
};

export function countActiveFilters(filters: CatalogFilters): number {
  return (Object.keys(EMPTY_FILTERS) as FilterGroupKey[]).reduce(
    (sum, key) => sum + filters[key].length,
    0,
  );
}

export function hasActiveFilters(filters: CatalogFilters): boolean {
  return countActiveFilters(filters) > 0;
}

export function toggleValue<T>(list: readonly T[], value: T): T[] {
  return list.includes(value) ? list.filter((x) => x !== value) : [...list, value];
}

// ——— Иерархия категорий (CAT-3) ———

export function parentSelection(
  selected: readonly number[],
  parent: CategoryOption,
): { on: boolean; kidsOn: number } {
  return {
    on: selected.includes(parent.id),
    kidsOn: parent.children.filter((child) => selected.includes(child.id)).length,
  };
}

/** Выбор родителя выбирает всех потомков; снятие родителя снимает потомков. */
export function toggleParentCategory(
  selected: readonly number[],
  parent: CategoryOption,
): number[] {
  const kids = parent.children.map((c) => c.id);
  if (selected.includes(parent.id))
    return selected.filter((id) => id !== parent.id && !kids.includes(id));
  return [...new Set([...selected, parent.id, ...kids])];
}

/** Выбор одного ребёнка при выбранном родителе снимает родителя и остальных детей. */
export function toggleChildCategory(
  selected: readonly number[],
  parent: CategoryOption,
  childId: number,
): number[] {
  const kids = parent.children.map((c) => c.id);
  if (selected.includes(parent.id)) {
    return [...selected.filter((id) => id !== parent.id && !kids.includes(id)), childId];
  }
  return toggleValue(selected, childId);
}

// ——— API ———

export function toApiParams(filters: CatalogFilters, query: string): VideoListParams {
  const params: VideoListParams = {};
  if (query) params.search = query;
  if (filters.category_ids.length) params.category_ids = filters.category_ids;
  if (filters.transition_type_ids.length) params.transition_type_ids = filters.transition_type_ids;
  if (filters.hook_ids.length) params.hook_ids = filters.hook_ids;
  if (filters.pacing.length) params.pacing = filters.pacing;
  if (filters.production_level.length) params.production_level = filters.production_level;
  if (filters.orientation.length) params.orientation = filters.orientation;
  if (filters.tutorial_platforms.length) params.tutorial_platforms = filters.tutorial_platforms;
  for (const key of filters.features) params[key] = true;
  return params;
}

// ——— URL (CAT-4) ———

const URL_KEYS: Record<FilterGroupKey, string> = {
  category_ids: 'cat',
  transition_type_ids: 'fx',
  hook_ids: 'hook',
  pacing: 'pace',
  production_level: 'lvl',
  orientation: 'orient',
  tutorial_platforms: 'tut',
  features: 'feat',
};
const QUERY_KEY = 'q';
const featureByUrl = new Map(FEATURES.map((f) => [f.url, f.key]));
const featureToUrl = new Map(FEATURES.map((f) => [f.key, f.url]));

function parseIds(raw: string | null): number[] {
  if (!raw) return [];
  return [
    ...new Set(
      raw
        .split(',')
        .map((s) => Number(s))
        .filter((n) => Number.isInteger(n) && n > 0),
    ),
  ];
}

function parseEnum<T extends string>(raw: string | null, allowed: readonly T[]): T[] {
  if (!raw) return [];
  return [
    ...new Set(raw.split(',').filter((s): s is T => (allowed as readonly string[]).includes(s))),
  ];
}

/** Читает фильтры и запрос из адреса; мусор игнорируется. */
export function filtersFromSearchParams(params: URLSearchParams): {
  filters: CatalogFilters;
  query: string;
} {
  return {
    query: params.get(QUERY_KEY) ?? '',
    filters: {
      category_ids: parseIds(params.get(URL_KEYS.category_ids)),
      transition_type_ids: parseIds(params.get(URL_KEYS.transition_type_ids)),
      hook_ids: parseIds(params.get(URL_KEYS.hook_ids)),
      pacing: parseEnum(params.get(URL_KEYS.pacing), PACING_VALUES),
      production_level: parseEnum(params.get(URL_KEYS.production_level), LEVEL_VALUES),
      orientation: parseEnum(params.get(URL_KEYS.orientation), ORIENTATION_VALUES),
      tutorial_platforms: parseEnum(
        params.get(URL_KEYS.tutorial_platforms),
        TUTORIAL_PLATFORMS.map((t) => t.code),
      ),
      features: (params.get(URL_KEYS.features) ?? '')
        .split(',')
        .map((u) => featureByUrl.get(u))
        .filter((k): k is FeatureKey => !!k),
    },
  };
}

/** Пишет фильтры и запрос в адрес, не трогая посторонние параметры (`?video=`). */
export function filtersToSearchParams(
  filters: CatalogFilters,
  query: string,
  base?: URLSearchParams,
): URLSearchParams {
  const next = new URLSearchParams(base);
  const setList = (key: string, values: readonly (string | number)[]) => {
    if (values.length) next.set(key, values.join(','));
    else next.delete(key);
  };
  if (query) next.set(QUERY_KEY, query);
  else next.delete(QUERY_KEY);
  setList(URL_KEYS.category_ids, filters.category_ids);
  setList(URL_KEYS.transition_type_ids, filters.transition_type_ids);
  setList(URL_KEYS.hook_ids, filters.hook_ids);
  setList(URL_KEYS.pacing, filters.pacing);
  setList(URL_KEYS.production_level, filters.production_level);
  setList(URL_KEYS.orientation, filters.orientation);
  setList(URL_KEYS.tutorial_platforms, filters.tutorial_platforms);
  setList(
    URL_KEYS.features,
    filters.features.map((k) => featureToUrl.get(k)).filter((u): u is string => !!u),
  );
  return next;
}

/** Снимок для аналитики filter_change / filters_cleared. */
export function filtersSnapshot(filters: CatalogFilters): Record<string, unknown> {
  const snapshot: Record<string, unknown> = {};
  for (const key of Object.keys(EMPTY_FILTERS) as FilterGroupKey[]) {
    if (filters[key].length) snapshot[key] = filters[key];
  }
  return snapshot;
}
